import React from 'react';
import {
    Card,
    DatePicker,
    WarningModal,
    ExtendedSelect,
    IExtendedSelectEvent,
    LoaderBtn,
    IExtendedSelectOption,
    CustomSwitch,
    InputText,
} from 'components';
import { ExamType, MappedExamTypes } from 'components/enums';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { push } from 'connected-react-router';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { getMoscowTime } from '_helpers';
import styles from './EditExamContainer.module.scss';
import moment from 'moment';
import cn from 'classnames';

const EditExamContainer = ({
    exam,
    onPush,
    getExpertsByIds,
    setFormData,
    onCleanError,
    experts,
    onCreate,
    apiError,
    initAsync,
    getQecQualificationsAsync,
    clearExpertsList,
}: Props) => {
    const [processing, setProcessing] = React.useState(false);
    const [showModal, setShowModal] = React.useState(false);
    const [modalTitle, setModalTitle] = React.useState('');
    const [modalContent, setModalContent] = React.useState('');
    const [formErrors, setFormErrors] = React.useState<IFieldsErrors>({ qualificationId: '', applicationsLimit: '' });
    const [showApplicationsLimit, setShowApplicationsLimit] = React.useState(exam.applicationsLimit !== undefined);

    React.useEffect(() => {
        const fetch = async () => {
            setProcessing(true);
            const {
                qualificationId, qualificationEvaluationCenterId, qualificationInfos, qecs,
            } = exam;

            if (!qualificationEvaluationCenterId && !qualificationId && !(qualificationInfos || qecs)) {
                await initAsync();
            }

            setProcessing(false);
        };

        fetch();
    }, []);

    React.useEffect(() => {
        const { invitedExpertIds, qecs, qualificationInfos } = exam;
        if (qecs && qecs.length === 0 && qualificationInfos && qualificationInfos.length === 0) {
            onPush('/exam-list');
        }
        if (invitedExpertIds.length !== 0) {
            getExpertsByIds(invitedExpertIds);
        }
    }, [exam]);


    const handleSelectChange = async (e: IExtendedSelectEvent) => {
        const { name, value } = e;

        if (name === 'qualificationEvaluationCenterId') {
            setFormData({ qualificationEvaluationCenterId: value, qualificationId: '' });
            await getQecQualificationsAsync();
        }

        if (!qualificationsList) return;

        const qualificationId = value;
        const selectedQualification = qualificationsList.find(q => q.id === qualificationId);

        if (selectedQualification) {
            setFormErrors({ qualificationId: '' });
            setFormData({ qualificationId, examType: ExamType.Intramural });
            onCleanError();
        }
    };

    const handleExamTypeChange = (e: IExtendedSelectEvent) => {
        setFormData({ examType: e.value as ExamType });
        onCleanError();
    };

    const handleDateChange = (val: string, momentDate: moment.Moment) => {
        const examDate = exam.examDate.clone().local();

        examDate.year(momentDate.year());
        examDate.month(momentDate.month());
        examDate.date(momentDate.date());
        setFormData({ examDate: moment.utc(examDate) });
        onCleanError();
    };

    const handleHoursChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const hour = parseInt(e.target.value);
        const examDate = exam.examDate.clone().local();
        examDate.hour(hour);

        setFormData({ examDate: examDate.clone().utc() });
        onCleanError();
    };

    const handleMinutesChange = (e: React.FormEvent<HTMLSelectElement>) => {
        const minute = parseInt(e.currentTarget.value);
        const examDate = exam.examDate.clone().local();

        examDate.minute(minute);
        setFormData({ examDate: moment.utc(examDate) });
        onCleanError();
    };

    const clearState = () => {
        setFormData({
            qualificationId: '',
            invitedExpertIds: [],
            examDate: moment(),
        });
    };

    const handleRejectBtn = () => {
        clearState();
        onPush('/exam-list');
    };

    const handleModal = () => {
        setShowModal(false);
        setModalTitle('');
        setModalContent('');
    };

    const createExam = async () => {
        if (showApplicationsLimit && !exam.applicationsLimit) {
            setFormErrors({ applicationsLimit: 'Укажите количество мест' });
            return;
        }

        if (!experts || experts.items.length < 3) {
            setShowModal(true);
            setModalTitle('Ошибка создания экзамена!');
            setModalContent('Экспертная комиссия должна состоять из 3-х экспертов.');
            return;
        }

        setProcessing(true);

        const res = await onCreate({ ...exam, type: exam.examType || ExamType.Intramural });

        if (res) {
            clearExpertsList();
            onPush('/exam-list');
        }

        setProcessing(false);
    };

    const appendExpert = () => {
        if (exam.qualificationId === '') {
            setFormErrors({ qualificationId: 'Выберите квалификацию' });
            return;
        }
        onPush('/new-exam/edit-expert');
    };

    const onClickSwitch = () => {
        setFormData({ applicationsLimit: undefined });
        setShowApplicationsLimit(!showApplicationsLimit);
    };

    const renderExperts = () => (experts ? experts.items.map(expert => (
        <span key={`expert_${expert.id} `} className={cn('d-block mb-2', styles.expertTitle)}>
            {expert.chairman || expert.id === exam.committeeChairmanId ? 'Председатель экспертной комиссии: ' : 'Член экспертной комиссии: '}
            <strong>{`${expert.lastName} ${expert.firstName} ${expert.middleName}`}</strong>
        </span>
    )) : null);

    const { qualificationEvaluationCenterId, qecs, qualificationId } = exam;
    const examDate = exam.examDate.clone().local();
    const { moscowHours, moscowMinutes } = getMoscowTime(examDate);
    const qualificationsList = exam.qecQualificationInfos;
    const startOfYear = moment().date(1).month(0).hour(0)
        .minute(0);

    const qualificationEvaluationCenterOptions = (qecs && qecs.map(m => ({ value: m.id, label: m.fullName }))) || [];
    const qualificationOptions = (qualificationsList && qualificationsList.map(m => ({ value: m.id, label: m.title }))) || [];
    const hasExperts = experts && experts.items.length > 0;

    const createExamTypeOptions = (): IExtendedSelectOption[] => {
        if (!qualificationsList) return [];

        const options = [({ value: ExamType.Intramural, label: MappedExamTypes[ExamType.Intramural].text })];
        const selectedQualification = qualificationsList.find(q => q.id === exam.qualificationId);

        if (selectedQualification && selectedQualification.allowOnline) {
            options.push({ value: ExamType.Online, label: MappedExamTypes[ExamType.Online].text });
        }

        if (selectedQualification && selectedQualification.allowRecertification) {
            options.push({ value: ExamType.Recertification, label: MappedExamTypes[ExamType.Recertification].text });
        }

        return options;
    };

    const examTypeOptions = createExamTypeOptions();

    return (
        <Card
          title="Создание нового экзамена"
          category="Добавление информации об экзамене и назначение экспертов"
          showLoader={processing}
          apiError={apiError}
        >
            <div className={styles.fields}>
                <div className={styles.field_title}>ЦОК</div>
                <div className={styles.field_value}>
                    <ExtendedSelect
                      name="qualificationEvaluationCenterId"
                      value={qualificationEvaluationCenterId}
                      options={qualificationEvaluationCenterOptions}
                      onChange={handleSelectChange}
                    />
                </div>
            </div>

            <div className={styles.fields}>
                <div className={styles.field_title}>КВАЛИФИКАЦИЯ</div>
                <div className={styles.field_value}>
                    <ExtendedSelect
                      name="qualificationId"
                      value={qualificationId}
                      placeholder="Выберите квалификацию"
                      options={qualificationOptions}
                      onChange={handleSelectChange}
                      isInvalid={Boolean(formErrors.qualificationId && formErrors.qualificationId.length > 0)}
                      invalidTitle={formErrors.qualificationId}
                    />
                </div>
            </div>

            {exam.qualificationId ? (
                <div className={styles.fields}>
                    <div className={styles.field_title}>ТИП ЭКЗАМЕНА</div>
                    <div className={styles.field_value}>
                        <ExtendedSelect
                          name="examType"
                          value={exam.examType}
                          placeholder="Тип экзамена"
                          options={examTypeOptions}
                          onChange={handleExamTypeChange}
                          disabled={examTypeOptions.length < 2}
                        />
                    </div>
                </div>
            ) : null}

            <div className={styles.fields}>
                <div className={styles.field_title}>ДАТА ЭКЗАМЕНА</div>
                <div className={`${styles.field_value} ${styles.datePicker}`}>
                    <DatePicker from={0} to={2} onChange={handleDateChange} initialDate={examDate} />
                </div>
            </div>

            <div className={styles.fields}>
                <div className={styles.field_title}>ВРЕМЯ ЭКЗАМЕНА</div>
                <div className={cn(styles.field_value, styles.timeValue)}>
                    <div className={styles.timeContainer}>
                        <select value={examDate.hour()} onChange={handleHoursChange}>
                            {([1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4]).map((m, i) => <option key={i} value={i}>{(`0${i}`).slice(-2)}</option>)}
                        </select>
                        <span>:</span>
                        <select value={examDate.minute()} onChange={handleMinutesChange}>
                            {([0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]).map(m => (
                                <option key={m} value={m}>{(`0${m}`).slice(-2)}</option>
                            ))}
                        </select>
                    </div>
                    <span className="ml-4">По МСК {`${moscowHours}:${moscowMinutes}`}</span>
                </div>
            </div>

            <div className={styles.fields}>
                <div className={styles.field_title}>КОЛИЧЕСТВО МЕСТ</div>
                <div className={`${styles.field_value} ${styles.applicationsLimit}`}>
                    <CustomSwitch
                      name="applicationsLimitShowSwitch"
                      onClick={onClickSwitch}
                      value={showApplicationsLimit}
                    />
                    {showApplicationsLimit ? (
                        <InputText
                          value={exam.applicationsLimit ? `${exam.applicationsLimit}` : ''}
                          name="applicationsLimit"
                          onChange={(name, value) => {
                              setFormData({ applicationsLimit: parseInt(value) });
                              setFormErrors({ ...formErrors, applicationsLimit: '' });
                            }}
                          maxLength={3}
                          inputColSize={2}
                          inputGroupClassNames="mb-0 ml-3 w-100"
                          fieldFailures={formErrors}
                          withoutLabel
                        />
                    ) : null}
                </div>
            </div>

            <div className={cn(styles.fields, 'mb-0', {
                'align-items-start': experts && experts.items.length !== 0,
                'align-items-center': experts && experts.items.length === 0,
            })}
            >
                <div className={styles.field_title}>КОМИССИЯ НОК</div>
                <div className={styles.field_value}>
                    {renderExperts()}

                    <Button
                      variant={hasExperts ? 'outline-primary' : 'primary'}
                      onClick={appendExpert}
                      className={cn(styles.createBtn, hasExperts && styles.changeExpertsBtn)}
                    >
                        {hasExperts ? 'Редактировать список' : 'Назначить экспертов'}
                    </Button>
                </div>
            </div>

            <div className={styles.buttons_container}>
                {apiError && <p className="text-danger mb-0 p-0">{apiError.message}</p>}

                {hasExperts ? (
                    <>
                        <LoaderBtn
                          onClick={createExam}
                          className={styles.createBtn}
                          disabled={examDate < startOfYear || processing}
                          loading={processing}
                        >
                            Создать экзамен
                        </LoaderBtn>
                        <Button variant="outline-secondary" onClick={handleRejectBtn} className={styles.cancelBtn}>
                            Отмена
                        </Button>
                    </>
                ) : null}
            </div>
            <WarningModal
              show={showModal}
              title={modalTitle}
              onHide={handleModal}
            >
                <p>{modalContent}</p>
            </WarningModal>
        </Card>
    );
};

const mapState = (state: IRootState) => ({
    exam: state.exam,
    experts: state.experts.expertsList,
    apiError: state.errors.error,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onPush: (key: string) => dispatch(push(key)),
    onCreate: dispatch.exam.createAsync,
    setFormData: dispatch.exam.setFormData,
    getExpertsByIds: dispatch.experts.getExpertsByIds,
    initAsync: dispatch.exam.initAsync,
    onCleanError: dispatch.errors.clean,
    getQecQualificationsAsync: dispatch.exam.getQecQualificationsAsync,
    clearExpertsList: dispatch.experts.clearExpertsList,
});

export default connect(mapState, mapDispatch)(EditExamContainer);

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>
type Props = connectedProps

interface IFieldsErrors {
    [key: string]: string;
}
